/**
 * Phase 5 §7 — Award-engine selector.
 *
 * Dispatches on `award_code` to the per-award calculator engine.
 * Unknown codes fall back to SCHADS, preserving the pre-Phase-5
 * default behaviour (and matching the Phase 1 acceptance reality
 * that all current callers were SCHADS-only).
 */

import type { ShiftPayBreakdown, ShiftPayInput, ShiftPayWithOvertimeInput } from '../schads';
import type { AwardCode, AwardEngine } from './types';
import { unavailableEvaluationTrace } from './evaluation-trace';
import { agedCareEngine } from './aged-care';
import { hpssEngine } from './hpss';
import { nursesEngine } from './nurses';
import { schadsEngine } from './schads';
import { zeroRecallBreakdown, type RecallPayBreakdown, type RecallPayInput } from './recall';

export type { AwardCode, AwardEngine } from './types';
export { assertEvaluationTrace, buildNativeEvaluationTrace } from './evaluation-trace';
export type { EvaluationTrace, EvaluationTraceInterval } from './evaluation-trace';
export { SchadsEngine, schadsEngine } from './schads';
export { NursesEngine, nursesEngine } from './nurses';
export {
  NURSES_HANDOVER_AGREEMENT_CODE,
  NURSES_HANDOVER_CLAUSE_REF,
  NURSES_HANDOVER_SCHEDULE_2_FRACTION,
  groupNursesHandoverLines,
  nursesHandoverCategory,
  nursesHandoverLineAmount,
  nursesHandoverLineCents,
  nursesHandoverPricing,
  nursesHandoverUnitRate,
  type NursesHandoverClassificationInput,
  type NursesHandoverGroupedLine,
} from './nurses';
export type {
  NursesHandoverCategory,
  NursesHandoverPricing,
  NursesShiftPayBreakdown,
} from './types';
export { AgedCareEngine, agedCareEngine } from './aged-care';
export {
  AGED_CARE_PUBLIC_HOLIDAY_ELECTION_CLAUSE,
  AGED_CARE_PUBLIC_HOLIDAY_ELECTION_CLAUSE_PART_TIME,
} from './aged-care';
export type {
  AgedCarePublicHolidayElection,
  AgedCarePublicHolidayElectionOutcome,
  AgedCareShiftPayBreakdown,
} from './aged-care';
export { HpssEngine, hpssEngine } from './hpss';
export {
  classifyShift,
  getOvertimeSegmentBreakdown,
  shiftCharacterToBandCode,
} from './shift-classifier';
export type {
  ClassifiedShift,
  ClassifyShiftOptions,
  DayType,
  DominantDayRule,
  ShiftCharacter,
  ShiftCharacterRules,
  ShiftSegment,
} from './shift-classifier';
export { calculateRecallPay, zeroRecallBreakdown, RecallBandLookupError } from './recall';
export type {
  RecallType,
  RecallPayBand,
  RecallPayInput,
  RecallRules,
  RecallPayBreakdown,
  RecallPayLine,
  OtRateBands,
  OtBandPair,
} from './recall';
export {
  calculateDisturbancePay,
  SCHADS_DISTURBANCE_RULES,
  AGED_CARE_DISTURBANCE_RULES,
} from './disturbance';
export {
  DISTURBANCE_AWARD_RULES_VERSION,
  disturbanceRulesForAward,
  disturbanceRulesUnsupportedReason,
} from '../award-data/disturbance-award-rules';
export type {
  DisturbancePayBand,
  DisturbancePayInput,
  PerEventDisturbanceInput,
  LesserOfWindowDisturbanceInput,
  DisturbanceRules,
  DisturbanceModel,
  DisturbanceEmploymentSubtype,
  DisturbancePayOptions,
  DisturbancePayBreakdown,
} from './disturbance';

/**
 * Strict no-op engine for unsupported awards. Returns ZERO pay + a warning
 * so a misrouted worker can't silently produce SCHADS-rate-derived dollar
 * amounts.
 *
 * `payroll-run-readiness` blocks unsupported awards from advancing to export
 * — this engine is the runtime backstop if that check is bypassed.
 */
class UnsupportedAwardEngine implements AwardEngine {
  readonly awardCode: AwardCode;
  // Tagged with sentinel strings so any `pay_run_item` accidentally
  // produced by this engine is unmistakable in audit / reconciliation
  // output. In practice `payroll-run-readiness` blocks this path before
  // finalisation, so these never reach the DB — but if the gate is
  // bypassed, the snapshot makes the failure explicit.
  readonly engineVersion: string = 'unsupported';
  readonly engineRevisionSha: string = 'unsupported';
  private readonly originalCode: string;

  constructor(originalCode: string) {
    this.awardCode = 'MA000100'; // Type-safe placeholder; the real check is in the warning.
    this.originalCode = originalCode;
  }

  private zero(): ShiftPayBreakdown {
    return {
      normalizedLevel: null,
      baseHourlyRate: 0,
      hoursWorked: 0,
      bands: {
        weekdayOrdinary: 0,
        saturdayOrdinary: 0,
        sundayOrdinary: 0,
        publicHoliday: 0,
        eveningShift: 0,
        nightShift: 0,
      },
      basePay: 0,
      penaltyPay: 0,
      shiftLoadingPay: 0,
      overtimeHours: 0,
      overtimePay: 0,
      overtimeFirstHours: 0,
      overtimeRemainderHours: 0,
      overtimePublicHolidayHours: 0,
      allowancesPay: 0,
      allowanceLines: [],
      grossTotal: 0,
      warnings: [
        `Award ${this.originalCode} is not yet supported — pay calculation produced $0. Workers under this award must be reviewed manually before payroll export. Phase 5 follow-up: implement engine + seed for ${this.originalCode}.`,
      ],
    };
  }

  calculate(_input: ShiftPayInput): ShiftPayBreakdown {
    return this.zero();
  }

  calculateWithOvertime(_input: ShiftPayWithOvertimeInput): ShiftPayBreakdown {
    return this.zero();
  }

  buildEvaluationTrace() {
    return unavailableEvaluationTrace('UNSUPPORTED_AWARD_ENGINE');
  }

  calculateRecall(_input: RecallPayInput): RecallPayBreakdown {
    return zeroRecallBreakdown(this.originalCode);
  }
}

/**
 * Select the engine for an award. Returns a strict no-op zero engine for
 * unsupported codes — no silent SCHADS fallback. `payroll-run-readiness`
 * is the primary gate; this engine is the runtime backstop.
 */
export function awardEngineFor(awardCode: string | AwardCode | null | undefined): AwardEngine {
  switch (awardCode) {
    case 'MA000100':
      return schadsEngine;
    case 'MA000034':
      return nursesEngine;
    case 'MA000018':
      return agedCareEngine;
    case 'MA000027':
      return hpssEngine;
    case null:
    case undefined:
    case '':
      // No award classified — must NOT silently fall back to SCHADS, since
      // that produces real-dollar pay calculations for an unclassified worker.
      // Return strict zero with a warning so payroll-run-readiness's
      // "Award classification is missing" blocker is the only path.
      return new UnsupportedAwardEngine('NULL_AWARD');
    default:
      return new UnsupportedAwardEngine(String(awardCode));
  }
}
